import { useCallback, useEffect, useState } from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import { toast } from "@/hooks/use-toast";
import { isStandaloneDisplay } from "@/lib/pwaInstall";
import {
  disableJobPushNotifications,
  enableJobPushNotifications,
  getJobPushStatus,
  type JobPushStatus,
} from "@/lib/jobPushNotifications";

type Props = {
  userId: string;
  className?: string;
  /** When true, skip the card chrome (parent already has a section title). */
  embedded?: boolean;
};

/**
 * Cleaner / assignee: turn job checkout alerts on or off (Home Screen web push).
 */
export function JobAlertsSettings({ userId, className, embedded }: Props) {
  const [status, setStatus] = useState<JobPushStatus | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    const s = await getJobPushStatus(userId);
    setStatus(s);
  }, [userId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const on = Boolean(status?.subscribed || (status?.enabledLocally && status?.permission === "granted"));
  const standalone = isStandaloneDisplay();

  const setEnabled = async (wantOn: boolean) => {
    setBusy(true);
    try {
      if (wantOn) {
        if (!standalone) {
          toast({
            title: "Add Stravo to your Home Screen first",
            description: "On iPhone use Safari → Share → Add to Home Screen, then open the app icon and turn alerts on.",
          });
          return;
        }
        const result = await enableJobPushNotifications(userId);
        if (result === "ok") {
          toast({ title: "Job alerts on", description: "We’ll notify you when checkout status changes on your jobs." });
        } else if (result === "denied") {
          toast({
            title: "Notifications blocked",
            description: "Allow notifications for Stravo in iPhone Settings → Notifications.",
            variant: "destructive",
          });
        } else if (result === "no_vapid") {
          toast({ title: "Alerts not configured", description: "Push keys are missing — ask your host to finish setup.", variant: "destructive" });
        } else {
          toast({
            title: "Not supported on this device",
            description: "Job alerts need a Home Screen install (Safari on iPhone).",
            variant: "destructive",
          });
        }
      } else {
        await disableJobPushNotifications(userId);
        toast({ title: "Job alerts off", description: "You won’t get checkout push notifications on this phone." });
      }
      await refresh();
    } catch (e) {
      toast({
        title: "Couldn’t update alerts",
        description: e instanceof Error ? e.message : "Try again",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  if (!status) {
    return (
      <div className={cn(embedded ? "py-2" : "rounded-[16px] border border-[#d7e0e6] bg-white p-4", className)}>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading alerts…
        </div>
      </div>
    );
  }

  const row = (
    <div className="flex items-start gap-3">
      {!embedded ? (
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#e7f6f4]">
          {on ? <Bell className="h-5 w-5 text-[#15776d]" /> : <BellOff className="h-5 w-5 text-[#64748b]" />}
        </div>
      ) : null}
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-center justify-between gap-3">
          <p className={cn("font-bold text-[#0f1720]", embedded ? "text-[15px]" : "text-[16px]")}>
            {embedded ? "Checkout push notifications" : "Job alerts"}
          </p>
          <Switch
            checked={on}
            disabled={busy || !status.supported || !status.vapidConfigured}
            onCheckedChange={(v) => void setEnabled(v)}
            aria-label="Job alerts"
          />
        </div>
        {!embedded ? (
          <p className="text-[13px] font-medium leading-relaxed text-[#64748b]">
            Push when a guest leaves, checkout moves, or you’re waiting past Out — only for jobs assigned to you.
          </p>
        ) : null}
        {!standalone ? (
          <p className="text-[12px] font-medium text-[#9a6700]">
            Open Stravo from your Home Screen icon to enable (Safari → Add to Home Screen).
          </p>
        ) : null}
        {status.permission === "denied" ? (
          <p className="text-[12px] font-medium text-[#b4453a]">
            Notifications are blocked in system settings for this app.
          </p>
        ) : null}
        {!status.supported ? (
          <p className="text-[12px] font-medium text-[#64748b]">This browser doesn’t support Home Screen push.</p>
        ) : null}
      </div>
    </div>
  );

  if (embedded) {
    return <div className={cn(className)}>{row}</div>;
  }

  return (
    <div className={cn("rounded-[16px] border border-[#d7e0e6] bg-white p-4 shadow-sm", className)}>
      {row}
    </div>
  );
}
